/**
 * Canonical route paths.
 *
 * Use these constants for navigation, redirects and `<Link to>` targets so a
 * path rename happens in exactly one place. Nested route definitions use
 * relative segments (e.g. `verification`), which resolve against the app
 * shell's parent route.
 */
export const ROUTES = {
  // Public
  signIn: '/signin',
  signUp: '/signup',
  forgotPassword: '/forgot-password',
  /** Public legal documents — linked from the Google OAuth consent screen. */
  privacy: '/privacy',
  terms: '/terms',

  // Authenticated
  dashboard: '/',
  /** Post-signup onboarding: approval status + activation OTP. */
  activation: '/activate',
  /** Verified account that has no group yet — no dashboard access until RBAC is set up. */
  awaitingAccess: '/awaiting-access',
  profile: '/profile',
  settings: '/settings',
  settingsGeneral: '/settings/general',
  settingsPassword: '/settings/password',
  forbidden: '/forbidden',

  // Audit modules (permission-gated) — mirrors the approved sidebar.
  engagements: '/engagements',
  auditPlan: '/audit-plan',
  auditPlanArchived: '/audit-plan/archived',
  /** System-wide, filterable audit trail (the `users:view` permission). */
  auditTrail: '/audit-trail',

  // Administration (permission-gated)
  staff: '/staff',
  staffArchived: '/staff/archived',
  groups: '/groups',

  // Design samples (no auth) — prototypes for review, not production screens.
  rolesPermissionsSample: '/sample/roles-permissions',
} as const

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES]

/** Nested segments under the groups route. */
const GROUP_SEGMENTS = { create: 'new', edit: 'edit' } as const

/** Nested segments under the staff route. */
const STAFF_SEGMENTS = { create: 'new' } as const

/** Detail route for a single staff account: `/staff/:id`. */
export function staffDetailPath(id: string): string {
  return `/staff/${id}`
}

/** Create route for a new staff account: `/staff/new`. */
export function staffCreatePath(): string {
  return `${ROUTES.staff}/${STAFF_SEGMENTS.create}`
}

/** Create route for a new group: `/groups/new`. */
export function groupCreatePath(): string {
  return `${ROUTES.groups}/${GROUP_SEGMENTS.create}`
}

/** Read-only detail route for a single group: `/groups/:id`. */
export function groupDetailPath(id: string): string {
  return `${ROUTES.groups}/${id}`
}

/** Edit route for a single group: `/groups/:id/edit`. */
export function groupEditPath(id: string): string {
  return `${ROUTES.groups}/${id}/${GROUP_SEGMENTS.edit}`
}

/** Nested segments under the audit plan route. */
const AUDIT_PLAN_SEGMENTS = { create: 'new', edit: 'edit' } as const

/** Create route for a new audit plan: `/audit-plan/new`. */
export function auditPlanCreatePath(): string {
  return `${ROUTES.auditPlan}/${AUDIT_PLAN_SEGMENTS.create}`
}

/** Read-only detail route for a single audit plan: `/audit-plan/:id`. */
export function auditPlanDetailPath(id: string): string {
  return `${ROUTES.auditPlan}/${id}`
}

/** Detail route for one audit-trail entry: `/audit-trail/:id`. */
export function auditTrailDetailPath(id: string): string {
  return `${ROUTES.auditTrail}/${id}`
}

/** Edit route for a single audit plan: `/audit-plan/:id/edit`. */
export function auditPlanEditPath(id: string): string {
  return `${ROUTES.auditPlan}/${id}/${AUDIT_PLAN_SEGMENTS.edit}`
}

/** Nested segments under the Audit Engagements module route. */
const AUDIT_ENGAGEMENT_SEGMENTS = { findings: 'findings', reports: 'reports' } as const

/** Findings page of the Audit Engagements module: `/engagements/findings`. */
export function auditEngagementFindingsPath(): string {
  return `${ROUTES.engagements}/${AUDIT_ENGAGEMENT_SEGMENTS.findings}`
}

/** Reports page of the Audit Engagements module: `/engagements/reports`. */
export function auditEngagementReportsPath(): string {
  return `${ROUTES.engagements}/${AUDIT_ENGAGEMENT_SEGMENTS.reports}`
}
